/**
 * Modern Product Card Data Manager
 * Uses GraphQL Storefront API to populate product cards with location-specific data
 */

// Debug mode - set to false for production
const PRODUCT_CARD_DEBUG = false;

// Cart icon used in the quick-add "add to cart" button state. Mirrors the SVG in
// card-product.liquid / card-product-search.liquid so a JS relabel matches the server markup.
const PRODUCT_CARD_ADD_SVG = '<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="m1 1c-.55228475 0-1 .44771525-1 1s.44771525 1 1 1h1.7792969a.69367162.69367162 35.784145 0 1 .65808707.47435058l3.2969911 9.8928369c.14641172.43923517.16502704.91215746.05273437 1.3613281l-.27148437 1.0878906c-.39834445 1.5933778.84195876 3.1835938 2.484375 3.1835938h12c.55228475 0 1-.44771525 1-1s-.44771525-1-1-1h-12c-.38938619 0-.6393619-.31950554-.54492188-.69726562l.20473297-.81770594a.64062109.64062109 142.02823 0 1 .6214389-.48502844h10.71875c.43057012.00022511.81295428-.27515444.94921875-.68359375l2.6660156-8c.21596464-.64778704-.26638007-1.3167178-.94921876-1.3164062h-16.111328a.69371294.69371294 35.782526 0 1 -.65811388-.47434165l-.94735487-2.8420646c-.13626447-.40843931-.51864863-.68381886-.94921875-.68359375zm7 19c-1.1045695 0-2 .8954305-2 2s.8954305 2 2 2 2-.8954305 2-2-.8954305-2-2-2zm12 0c-1.1045695 0-2 .8954305-2 2s.8954305 2 2 2 2-.8954305 2-2-.8954305-2-2-2z"/></svg>';

class ProductCardDataManager {
  constructor() {
    this.DEBUG = PRODUCT_CARD_DEBUG;
    this.locationManager = window.locationManager;
    this.batchQueue = [];
    this.batchTimeout = null;
    this.batchDelay = 100; // 100ms debounce
    this.isProcessing = false;
    this._addLabelMarkup = null; // cached "add to cart" label markup
  }

  /**
   * Markup for the quick-add "add to cart" label. Captured once from an in-stock card on the
   * page so it matches the theme/locale exactly; falls back to a constant if none is present.
   */
  getAddLabelMarkup() {
    if (this._addLabelMarkup) return this._addLabelMarkup;
    const refSvg = document.querySelector('.quick-add__submit > span:not(.sold-out-message) svg');
    if (refSvg) {
      const span = refSvg.closest('span');
      if (span && span.innerHTML.trim()) {
        this._addLabelMarkup = span.innerHTML;
        return this._addLabelMarkup;
      }
    }
    this._addLabelMarkup = 'Add to cart ' + PRODUCT_CARD_ADD_SVG;
    return this._addLabelMarkup;
  }

  /**
   * Relabel a quick-add button to its "add to cart" state.
   * Availability here is location-based: when a card is confirmed in stock at the picking
   * store, the server-rendered "Sold out" label (driven by Shopify's global variant.available,
   * which ignores the picking store) must be corrected to the add state. Cards NOT available at
   * the location are removed/hidden elsewhere, so this only runs on genuinely-addable cards.
   */
  restoreAddButtonLabel(container) {
    try {
      const submit = container.querySelector('.quick-add__submit, .product-form__submit');
      if (!submit) return;
      const label = submit.querySelector(':scope > span:not(.sold-out-message)');
      if (!label || label.querySelector('svg')) return; // missing, or already in add state
      label.innerHTML = this.getAddLabelMarkup();
    } catch (e) {
      if (this.DEBUG) console.warn('restoreAddButtonLabel failed:', e);
    }
  }

  isEachUom(uom) {
    const raw = (uom || '').toString().trim().toLowerCase();
    const normalized = raw
      .replace(/\$/g, '')
      .replace(/\d+(?:\.\d+)?/g, '')
      .replace(/\s+/g, ' ')
      .trim();

    return normalized === 'each' ||
      normalized === 'ea' ||
      normalized.includes('/ each') ||
      normalized.includes('per each') ||
      /(^|[\s/])each\b/.test(normalized) ||
      /(^|\s)ea\b/.test(normalized);
  }

  isProduceCard(productCard, productData = null) {
    const productType = [
      productCard?.getAttribute('data-product-type'),
      productData?.productType
    ].filter(Boolean).join(' ').toLowerCase();
    const productTags = [
      productCard?.getAttribute('data-product-tags'),
      Array.isArray(productData?.tags) ? productData.tags.join(' ') : productData?.tags
    ].filter(Boolean).join(' ').toLowerCase();
    const hasProducePriceClass = Boolean(productCard?.querySelector('.unit_price.price-special-color'));

    return hasProducePriceClass ||
      productType.includes('fruit') ||
      productType.includes('veg') ||
      productTags.includes('fruit') ||
      productTags.includes('vegetable');
  }

  hideUnitPriceDisplayForEachProduce(productCard, uom, targetElement, productData = null) {
    if (!targetElement) return false;

    if (this.isEachUom(uom) && this.isProduceCard(productCard, productData)) {
      targetElement.innerHTML = '';
      targetElement.style.display = 'none';
      targetElement.classList.add('unit-price-display--hidden');
      return true;
    }

    targetElement.style.display = '';
    targetElement.classList.remove('unit-price-display--hidden');
    return false;
  }

  /**
   * Initialize the product card data manager
   */
  init() {
    if (!this.locationManager) {
      console.error('LocationManager not found. Make sure location-manager.js is loaded first.');
      return;
    }

    if (this.DEBUG) console.log('ProductCardDataManager initialized');
    this.setupEventListeners();
    
    // Check if we're on a collection page (has product-grid with collection handle)
    const productGrid = document.getElementById('product-grid');
    const isCollectionPage = productGrid && productGrid.hasAttribute('data-collection-handle');
    const hasServerLocationFilter = window.location.search.includes('filter.p.m.custom.available_locations');
    
    if (isCollectionPage && !hasServerLocationFilter) {
      console.log('📦 Collection page detected - filtering by available_locations');
      this.filterCollectionByLocation();
    }
    
    // Always process all product cards for pricing
    this.processAllProductCards();
    
    this.processMainProductForm(); // Handle main product form on product pages
  }

  /**
   * Filter collection products by available_locations metafield
   * Hides products not available at the current location
   */
  filterCollectionByLocation() {
    const productGrid = document.getElementById('product-grid');
    const currentLocation = this.getCollectionLocation(productGrid);
    
    if (!productGrid) {
      console.warn('Product grid not found');
      return;
    }
    
    const productItems = productGrid.querySelectorAll('.product-item');
    let visibleCount = 0;
    let hiddenCount = 0;
    const hiddenEmptyLocations = [];
    const hiddenNotAtLocation = [];
    
    console.log(`🔍 Filtering ${productItems.length} products for location ${currentLocation}...`);
    
    productItems.forEach((item, index) => {
      const availableLocationsAttr = item.getAttribute('data-available-locations');
      const productHandle = item.getAttribute('data-product-handle') || `product-${index}`;
      
      // If no available_locations attribute, show the product (metafield not set yet)
      if (!availableLocationsAttr) {
        if (this.DEBUG) console.log(`   [${productHandle}] No available_locations - showing (metafield not set)`);
        visibleCount++;
        return;
      }
      
      try {
        const availableLocations = JSON.parse(availableLocationsAttr);
        
        // If empty array, hide the product (no inventory anywhere)
        if (!Array.isArray(availableLocations) || availableLocations.length === 0) {
          item.classList.add('location-unavailable');
          hiddenCount++;
          hiddenEmptyLocations.push(productHandle);
          return;
        }
        
        // Check if current location is in the available locations
        const isAvailable = availableLocations.includes(currentLocation) || 
                           availableLocations.includes(String(currentLocation));
        
        if (isAvailable) {
          item.classList.remove('location-unavailable');
          visibleCount++;
          if (this.DEBUG) console.log(`   [${productHandle}] ✓ Available at location ${currentLocation}`);
        } else {
          item.classList.add('location-unavailable');
          hiddenCount++;
          hiddenNotAtLocation.push(productHandle);
        }
      } catch (e) {
        // JSON parse error - show the product
        console.warn(`   [${productHandle}] Failed to parse available_locations:`, e);
        visibleCount++;
      }
    });
    
    console.log(`✅ Location filter complete: ${visibleCount} visible, ${hiddenCount} hidden`);
    // Always-on removal audit: exactly which cards this script hid, and why.
    if (hiddenEmptyLocations.length > 0) {
      console.log(`🗑️ Hidden (empty available_locations - no stores at all): ${hiddenEmptyLocations.length}`, hiddenEmptyLocations);
    }
    if (hiddenNotAtLocation.length > 0) {
      console.log(`🗑️ Hidden (not ranged at store ${currentLocation}): ${hiddenNotAtLocation.length}`, hiddenNotAtLocation);
    }
    
    // Update product count display
    this.updateProductCount(visibleCount);
    
    // Mark grid as filtered
    productGrid.classList.add('filtered');
    productGrid.classList.remove('filtering');
  }

  getCollectionLocation(productGrid = document.getElementById('product-grid')) {
    const urlLocation = new URL(window.location.href).searchParams.get('filter.p.m.custom.available_locations');

    return urlLocation ||
      productGrid?.getAttribute('data-location-to-use') ||
      this.locationManager.getCurrentLocation();
  }
  
  /**
   * Update the product count display
   * Only updates if URL doesn't have location filter (client-side filtering)
   * If URL has filter, Shopify did server-side filtering and Liquid count is correct
   */
  updateProductCount(count) {
    const urlHasFilter = window.location.search.includes('filter.p.m.custom.available_locations');
    
    // Always remove loading class
    document.querySelectorAll('.product-count__text').forEach(el => {
      el.classList.remove('loading');
    });
    
    if (typeof window.updateCollectionAvailabilityState === 'function') {
      window.updateCollectionAvailabilityState(count, this.getCollectionLocation());
    } else if (count === 0) {
      document.querySelectorAll('#main-collection-filters, .facets-vertical-sort, .pagination-wrapper').forEach(el => {
        el.hidden = true;
        el.style.setProperty('display', 'none', 'important');
      });
    }

    // Update count text for client-side filtering and for empty server-filtered collection pages.
    if (!urlHasFilter || count === 0) {
      const countElements = document.querySelectorAll('#ProductCount, #ProductCountDesktop');
      countElements.forEach(el => {
        el.textContent = `${count} products`;
      });
      console.log(`📊 Updated product count to: ${count} products (client-side filtered)`);
    } else {
      console.log(`📊 Product count preserved from Liquid (server-side filtered, page shows ${count})`);
    }
  }

  /**
   * Setup event listeners for location changes
   */
  setupEventListeners() {
    document.addEventListener('locationChanged', () => {
      console.log('Location changed, refreshing product cards...');
      
      // Check if we're on a collection page - re-filter by location
      const productGrid = document.getElementById('product-grid');
      const isCollectionPage = productGrid && productGrid.hasAttribute('data-collection-handle');
      const hasServerLocationFilter = window.location.search.includes('filter.p.m.custom.available_locations');
      
      if (isCollectionPage && !hasServerLocationFilter) {
        console.log('📦 Re-filtering collection by new location');
        this.filterCollectionByLocation();
      }
      
      // Always process all product cards on location change, force reprocessing
      this.processAllProductCards(true);
      
      this.processMainProductForm(); // Re-process main product form on location change
    });

    // Listen for DOM changes to process new product cards
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          const newProductCards = Array.from(mutation.addedNodes)
            .filter(node => node.nodeType === Node.ELEMENT_NODE)
            .flatMap(node => {
              if (node.classList?.contains('product-item')) {
                return [node];
              }
              return Array.from(node.querySelectorAll?.('.product-item') || []);
            })
            // Filter out GraphQL-generated cards (they're already complete)
            .filter(card => !card.hasAttribute('data-graphql-card') && !card.hasAttribute('data-processed'));

          if (newProductCards.length > 0) {
            console.log(`Found ${newProductCards.length} new product cards, processing...`);
            this.processProductCards(newProductCards);
          }
        }
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  /**
   * Process all product cards on the page
   */
  processAllProductCards(forceReprocess = false) {
    const productCards = document.querySelectorAll('.product-item');
    if (this.DEBUG) console.log(`Processing ${productCards.length} product cards...`);
    
    // If forcing reprocess (e.g., location change), remove data-processed attribute
    if (forceReprocess) {
      productCards.forEach(card => {
        if (!card.hasAttribute('data-graphql-card')) {
          card.removeAttribute('data-processed');
        }
      });
    }
    
    this.processProductCards(Array.from(productCards));
  }

  /**
   * Process a batch of product cards
   */
  processProductCards(productCards) {
    if (this.isProcessing) {
      console.log('Already processing cards, skipping...');
      return;
    }

    productCards.forEach(productCard => {
      this.processProductCard(productCard);
    });

    this.updateCartCollectionVisibility();
    
    // Mark product grid as loaded
    const productGrid = document.getElementById('product-grid');
    if (productGrid) {
      productGrid.classList.add('product-grid--loaded');
    }
    
    // Hide loading overlays based on page type
    const isSearchPage = document.body.classList.contains('template-search');
    const isCollectionPage = document.body.classList.contains('template-collection');
    
    if (isSearchPage) {
      // Hide search results loading overlay
      const resultsLoadingOverlay = document.querySelector('.results-loading-overlay');
      if (resultsLoadingOverlay) {
        resultsLoadingOverlay.style.display = 'none';
        console.log('✅ Hiding search loading overlay (product-card-data)');
      }
    }
    
    if (isCollectionPage) {
      // Hide collection loading overlay
      const loadingOverlay = document.querySelector('.loading-overlay');
      if (loadingOverlay) {
        loadingOverlay.style.display = 'none';
      }
      // Add collection class to show products are ready
      const collection = document.querySelector('.collection');
      if (collection) {
        collection.classList.remove('is-loading');
      }
      
      // Recount visible products after pricing processing (some may have been hidden)
      this.recountVisibleProducts();
    }
  }

  updateCartCollectionVisibility() {
    document.querySelectorAll('.go-cart__collection').forEach(collection => {
      const items = Array.from(collection.querySelectorAll('.upsell-item'));
      // The drawer's empty-cart block is now category tiles with no product
      // cards at all - zero upsell items must not be treated as "everything
      // was hidden by location filtering".
      if (items.length === 0) {
        collection.classList.remove('location-empty');
        collection.setAttribute('data-location-validating', 'false');
        return;
      }
      const visibleItems = items.filter(item => {
        const card = item.querySelector('.product-card, .product-item');
        return card &&
          !card.classList.contains('location-unavailable') &&
          !card.classList.contains('hidden') &&
          card.style.display !== 'none';
      });

      collection.classList.toggle('location-empty', visibleItems.length === 0);
      collection.setAttribute('data-location-validating', 'false');
    });
  }
  
  /**
   * Recount visible products after processing
   */
  recountVisibleProducts() {
    const productGrid = document.getElementById('product-grid');
    if (!productGrid) return;
    
    const allItems = productGrid.querySelectorAll('.product-item');
    let visibleCount = 0;
    
    allItems.forEach(item => {
      // Count as visible if not hidden
      if (!item.classList.contains('location-unavailable') && 
          item.style.display !== 'none' &&
          !item.classList.contains('hidden')) {
        visibleCount++;
      }
    });
    
    console.log(`📊 Recounted visible products: ${visibleCount}`);
    this.updateProductCount(visibleCount);
  }

  /**
   * Process a single product card
   */
  processProductCard(productCard) {
    const productTitle = productCard.getAttribute('data-title') || 'Unknown';
    
    // Skip GraphQL-generated cards (they're already fully rendered)
    if (productCard.hasAttribute('data-graphql-card')) {
      return;
    }
    
    // Skip Liquid-rendered cards (search page with pure Liquid pricing)
    if (productCard.hasAttribute('data-liquid-rendered')) {
      return;
    }
    
    // Skip if already processed
    if (productCard.hasAttribute('data-processed')) {
      return;
    }

    if (this.DEBUG) console.log(`[ProcessCard] Starting: ${productTitle}`);

    if (productCard.hasAttribute('data-frozen') && productCard.getAttribute('data-frozen') === 'true') {
      if (this.DEBUG) console.log(`[${productTitle}] Removing frozen product`);
      this.removeProductCard(productCard);
      return;
    }

    // Check for JSON data attributes first (collection pages, search results, featured collections)
    // This is the primary path for Liquid-rendered pages
    try {
      const locationPricesJson = productCard.getAttribute('data-location-prices');
      if (locationPricesJson) {
        if (this.DEBUG) console.log(`[${productTitle}] Found JSON data attributes, processing...`);
        this.processProductCardWithJsonDataFromCard(productCard, locationPricesJson);
        productCard.setAttribute('data-processed', 'true');
        return;
      }
    } catch (e) {
      console.error(`[${productTitle}] Error processing JSON data attributes:`, e);
    }
    
    // Check for JSON data on nested product-form element (older format)
    try {
      const productForm = productCard.querySelector('product-form');
      const locationPricesJson = productForm?.getAttribute('data-location-prices');
      if (locationPricesJson) {
        if (this.DEBUG) console.log(`[${productTitle}] Found JSON data on product-form, processing...`);
        this.processProductCardWithJsonData(productCard, locationPricesJson);
        productCard.setAttribute('data-processed', 'true');
        return;
      }
    } catch (e) {
      console.error(`[${productTitle}] Error processing product-form JSON data:`, e);
    }

    // Check for very old variant-based system (has data-all-variants)
    const variants = productCard.getAttribute('data-all-variants');
    if (variants) {
      if (this.DEBUG) console.log(`[${productTitle}] Processing old variant-based system (data-all-variants)`);
      this.processLegacyProductCard(productCard, variants);
      productCard.setAttribute('data-processed', 'true');
      return;
    }

    // Check for legacy system with individual price attributes (predictive search)
    const currentLocation = this.locationManager.getCurrentLocation();
    const priceAttr = productCard.getAttribute(`data-price-location-${currentLocation}`);
    
    if (priceAttr !== null) {
      if (this.DEBUG) console.log(`[${productTitle}] Has individual price attribute, but missing data-all-variants`);
      // Card has price but no variant data - this shouldn't happen, but handle gracefully
      // Remove card if price is invalid
      if (priceAttr === '' || priceAttr === 'null' || parseFloat(priceAttr) <= 0) {
        if (this.DEBUG) console.log(`[${productTitle}] Invalid price, removing card`);
        this.removeProductCard(productCard);
        return;
      }
      // If we get here, card has a valid price but no variants - mark as processed
      productCard.setAttribute('data-processed', 'true');
      return;
    }

    // Check for new GraphQL system (collection pages)
    const productHandle = productCard.getAttribute('data-product-handle');
    if (productHandle) {
      if (this.DEBUG) console.log(`[${productTitle}] Found GraphQL product card with handle`);
      // New GraphQL system - add to batch queue for efficient processing
      this.addToBatchQueue(productHandle, productCard);
      return;
    }
    
    if (this.DEBUG) console.warn(`[${productTitle}] ⚠️ Product card missing all required data attributes:`, productCard);
    // Remove card if we can't process it
    this.removeProductCard(productCard);
  }

  /**
   * Helper method to remove a product card (and its carousel wrapper if present)
   */
  removeProductCard(productCard) {
    // Check if this card is inside a carousel slide wrapper
    const carouselSlide = productCard.closest('.upsell-item, .swiper-slide');
    if (carouselSlide && carouselSlide !== productCard) {
      console.log('   Removing carousel slide wrapper');
      carouselSlide.remove();
    } else {
      productCard.remove();
    }
  }

  /**
   * Process product card with JSON data attributes directly on the card element
   * Used for Liquid-rendered collection pages
   */
  processProductCardWithJsonDataFromCard(productCard, locationPricesJson) {
    const productGrid = productCard.closest('#product-grid');
    const pickingStore = this.getCollectionLocation(productGrid);
    const submitButton = productCard.querySelector('.submit-button');
    const productIDInput = productCard.querySelector('input[name="id"]');
    const locationPriceInput = productCard.querySelector('[data-location-price]');
    const locationIdInput = productCard.querySelector('[data-location-id]');
    const priceWrapper = productCard.querySelector('.dynamic-price__wrapper');
    const productTitle = productCard.getAttribute('data-title') || 'Unknown Product';
    
    try {
      // Parse JSON data attributes from the card element
      const locationPrices = JSON.parse(locationPricesJson);
      const locationSpecialsJson = productCard.getAttribute('data-location-specials');
      const locationSpecialTextsJson = productCard.getAttribute('data-location-special-texts');
      const locationUnitPricesJson = productCard.getAttribute('data-location-unit-prices');
      const locationAdditionalsJson = productCard.getAttribute('data-location-additionals');
      
      const locationSpecials = locationSpecialsJson ? JSON.parse(locationSpecialsJson) : {};
      const locationSpecialTexts = locationSpecialTextsJson ? JSON.parse(locationSpecialTextsJson) : {};
      const locationUnitPrices = locationUnitPricesJson ? JSON.parse(locationUnitPricesJson) : {};
      const locationAdditionals = locationAdditionalsJson ? JSON.parse(locationAdditionalsJson) : {};
      
      // Get UOM and conversion from card data attributes
      const uom = productCard.getAttribute('data-uom') || '';
      const conversionFactor = productCard.getAttribute('data-conversion-factor') || '1';
      const overrideCupValue = productCard.getAttribute('data-overridecupvalue') || '';
      
      // Get location-specific data
      const locationPrice = locationPrices[pickingStore];
      const locationSpecial = locationSpecials[pickingStore];
      const locationSpecialText = locationSpecialTexts[pickingStore];
      const locationUnitPrice = locationUnitPrices[pickingStore];
      const locationAdditional = locationAdditionals[pickingStore];
      
      if (this.DEBUG) console.log(`[${productTitle}] Location: ${pickingStore}, Price: "${locationPrice}"`);
      
      // Check available_locations - cart drawer recommendations should match Search & Discovery availability.
      const productForm = productCard.querySelector('product-form');
      const availableLocations = productCard.getAttribute('data-available-locations') ||
        productForm?.getAttribute('data-available-locations');
      const requireAvailableLocations = productCard.closest('.go-cart__collection');

      if (requireAvailableLocations && (!availableLocations || availableLocations === 'null' || availableLocations === '[]')) {
        console.log(`🗑️ [${productTitle}] Hidden card - available_locations missing/empty (cart recommendation)`);
        productCard.classList.add('location-unavailable');
        return;
      }

      if (availableLocations) {
        try {
          const locationsArray = JSON.parse(availableLocations);
          if (!Array.isArray(locationsArray) || locationsArray.length === 0) {
            console.log(`🗑️ [${productTitle}] Hidden card - empty available_locations`);
            productCard.classList.add('location-unavailable');
            return;
          }

          if (Array.isArray(locationsArray) && locationsArray.length > 0) {
            if (!locationsArray.includes(pickingStore) && !locationsArray.includes(String(pickingStore))) {
              console.log(`🗑️ [${productTitle}] Hidden card - not ranged at store ${pickingStore}`);
              productCard.classList.add('location-unavailable');
              return;
            }
          }
        } catch (e) {
          // Continue if parse fails
        }
      }
      
      // Remove card if no valid price for current location
      if (!locationPrice || locationPrice === '' || locationPrice === 'null' || parseFloat(locationPrice) <= 0) {
        console.log(`🗑️ [${productTitle}] Hidden card - no valid price for store ${pickingStore} (got "${locationPrice}")`);
        productCard.classList.add('location-unavailable');
        return;
      }
      
      // Update price display
      if (priceWrapper) {
        const fromPriceElement = priceWrapper.querySelector('.sale .from_price');
        if (fromPriceElement) {
          const priceFloat = parseFloat(locationPrice);
          const formattedPrice = this.formatPrice(Math.round(priceFloat * 100));
          fromPriceElement.innerHTML = formattedPrice;
        }
        
        // Update unit price display
        const unitPriceDisplay = priceWrapper.querySelector('.unit_price_display');
        if (unitPriceDisplay && this.hideUnitPriceDisplayForEachProduce(productCard, uom, unitPriceDisplay)) {
          // Fruit/veg sold as each already shows the each price in the main price.
        } else if (unitPriceDisplay && uom && uom.trim()) {
          const priceFloat = parseFloat(locationPrice);
          const unitPriceFloat = locationUnitPrice ? parseFloat(locationUnitPrice) : 0;
          const cleanUom = uom.trim().replace(/^\/\s*/, 'per ').replace(/^per\s+per\s+/, 'per ');
          
          // If no unit price OR override is "Yes", calculate
          if (!unitPriceFloat || unitPriceFloat === 0 || overrideCupValue === "Yes") {
            if (conversionFactor) {
              const conversionFloat = parseFloat(conversionFactor);
              const calculatedUnitPrice = (priceFloat * conversionFloat).toFixed(2);
              unitPriceDisplay.innerHTML = `<small>$${calculatedUnitPrice} ${cleanUom}</small>`;
            } else {
              unitPriceDisplay.innerHTML = '';
            }
          } else {
            // Use the unit price from metafield
            unitPriceDisplay.innerHTML = `<small>$${unitPriceFloat.toFixed(2)} ${cleanUom}</small>`;
          }
        }
        
        // Remove price-visually-hidden class to show price
        priceWrapper.classList.remove('price-visually-hidden');
      }
      
      // Update special badge and pricing
      const specialBadge = productCard.querySelector('.special-badge');
      const unitPriceElement = priceWrapper ? priceWrapper.querySelector('.unit_price') : null;
      const isOnSpecial = locationSpecial === true || locationSpecial === 'true' || locationSpecial === '1' || 
                          locationSpecial === 'Yes' || locationSpecial === 'yes' || 
                          locationSpecial === 'True' ||
                          (locationSpecialText && locationSpecialText !== '-' && locationSpecialText !== 'undefined' && locationSpecialText !== '');
      
      // Specials collections must only show products actually on special at
      // the current picking store (special_location_XX metafield).
      if (productGrid && productGrid.hasAttribute('data-specials-collection') && !isOnSpecial) {
        console.log(`🗑️ [${productTitle}] Hidden card - not on special at store ${pickingStore} (specials collection)`);
        productCard.classList.add('location-unavailable');
        return;
      }
      
      if (isOnSpecial) {
        if (specialBadge) specialBadge.style.display = 'block';
        if (unitPriceElement) unitPriceElement.classList.add('is-special');
      } else {
        if (specialBadge) specialBadge.style.display = 'none';
        if (unitPriceElement) unitPriceElement.classList.remove('is-special');
      }
      
      // Update additional text — the "Save $X" additional metafield only makes
      // sense while the product is on special, so it never shows otherwise.
      if (priceWrapper) {
        const additionalElement = priceWrapper.querySelector('.additional');
        if (additionalElement) {
          if (isOnSpecial && locationSpecialText && locationSpecialText !== '-' && locationSpecialText.trim()) {
            additionalElement.innerHTML = locationSpecialText;
            additionalElement.classList.add('show-additional');
          } else if (isOnSpecial && locationAdditional && locationAdditional.trim() && locationAdditional !== '-') {
            additionalElement.innerHTML = locationAdditional;
            additionalElement.classList.add('show-additional');
          } else {
            additionalElement.innerHTML = '';
            additionalElement.classList.remove('show-additional');
          }
        }
      }
      
      // Update location price inputs for cart
      if (locationPriceInput) locationPriceInput.value = locationPrice;
      if (locationIdInput) locationIdInput.value = pickingStore;
      
      // Enable the form
      if (productIDInput) productIDInput.disabled = false;
      if (submitButton) submitButton.disabled = false;
      
      // Show the card
      productCard.classList.add('card-in-stock');
      productCard.classList.remove('location-unavailable');
      this.restoreAddButtonLabel(productCard);
      
      if (this.DEBUG) console.log(`[${productTitle}] ✓ Enabled - Price: $${locationPrice}`);
      
    } catch (e) {
      console.error('Error processing card JSON data:', e);
      productCard.classList.add('location-unavailable');
    }
  }

  /**
   * Process product card with JSON data attributes (for search/predictive search)
   */
  processProductCardWithJsonData(productCard, locationPricesJson) {
    const pickingStore = this.locationManager.getCurrentLocation();
    const submitButton = productCard.querySelector('.submit-button');
    const productIDInput = productCard.querySelector('input[name="id"]');
    const locationPriceInput = productCard.querySelector('[data-location-price]');
    const locationIdInput = productCard.querySelector('[data-location-id]');
    const priceWrapper = productCard.querySelector('.dynamic-price__wrapper');
    const productTitle = productCard.getAttribute('data-title') || 'Unknown Product';
    
    try {
      // Parse JSON data attributes
      const locationPrices = JSON.parse(locationPricesJson);
      const locationSpecialsJson = productCard.getAttribute('data-location-specials');
      const locationSpecialTextsJson = productCard.getAttribute('data-location-special-texts');
      const locationUnitPricesJson = productCard.getAttribute('data-location-unit-prices');
      const locationAdditionalsJson = productCard.getAttribute('data-location-additionals');
      
      const locationSpecials = locationSpecialsJson ? JSON.parse(locationSpecialsJson) : {};
      const locationSpecialTexts = locationSpecialTextsJson ? JSON.parse(locationSpecialTextsJson) : {};
      const locationUnitPrices = locationUnitPricesJson ? JSON.parse(locationUnitPricesJson) : {};
      const locationAdditionals = locationAdditionalsJson ? JSON.parse(locationAdditionalsJson) : {};
      
      // Get location-specific data
      const locationPrice = locationPrices[pickingStore];
      const locationSpecial = locationSpecials[pickingStore];
      const locationSpecialText = locationSpecialTexts[pickingStore];
      const locationUnitPrice = locationUnitPrices[pickingStore];
      const locationAdditional = locationAdditionals[pickingStore];
      
      if (this.DEBUG) console.log(`[${productTitle}] Location: ${pickingStore}, Price: "${locationPrice}", Type: ${typeof locationPrice}`);
      
      // Check hideInState
      const hideInState = productCard.getAttribute('data-hideinstate') || '';
      if (hideInState && hideInState !== '-' && hideInState.includes(pickingStore.toString())) {
        if (this.DEBUG) console.log(`[${productTitle}] Hidden in location ${pickingStore}, removing card`);
        this.removeProductCard(productCard);
        return;
      }
      
      // Remove card if no valid price for current location
      if (!locationPrice || locationPrice === '' || locationPrice === 'null' || parseFloat(locationPrice) <= 0) {
        if (this.DEBUG) console.log(`[${productTitle}] No valid price for location ${pickingStore}, removing card`);
        this.removeProductCard(productCard);
        return;
      }
      
      // Check if location has inventory (available_locations metafield)
      // Only enforce if metafield exists - allows products to show until Flow populates the field
      const productForm = productCard.querySelector('product-form');
      const availableLocations = productForm ? productForm.getAttribute('data-available-locations') : null;
      
      if (availableLocations && availableLocations !== 'null' && availableLocations !== '') {
        if (this.DEBUG) console.log(`[${productTitle}] Checking available_locations: "${availableLocations}"`);
        try {
          const locationsArray = JSON.parse(availableLocations);
          
          // If array is empty, product has no inventory anywhere
          if (Array.isArray(locationsArray) && locationsArray.length === 0) {
            if (this.DEBUG) console.log(`[${productTitle}] No inventory at any location, removing card`);
            this.removeProductCard(productCard);
            return;
          }
          
          // If array exists with locations, check if current location is in it
          if (Array.isArray(locationsArray) && !locationsArray.includes(pickingStore.toString())) {
            if (this.DEBUG) console.log(`[${productTitle}] No inventory at location ${pickingStore}, removing card`);
            this.removeProductCard(productCard);
            return;
          }
        } catch (e) {
          if (this.DEBUG) console.warn(`[${productTitle}] Failed to parse available_locations "${availableLocations}":`, e);
          // Continue - don't hide on parse error
        }
      }
      
      // Update price display
      if (priceWrapper) {
        const fromPriceElement = priceWrapper.querySelector('.sale .from_price');
        if (fromPriceElement) {
          const priceFloat = parseFloat(locationPrice);
          const formattedPrice = this.formatPrice(Math.round(priceFloat * 100));
          fromPriceElement.innerHTML = formattedPrice;
        }
        
        // Update unit price display ($/kg, etc.) - always calculate using conversion factor
        const unitPriceDisplay = priceWrapper.querySelector('.unit_price_display');
        if (unitPriceDisplay) {
          // Get UOM, conversion factor, override cup value, and location unit price
          const productForm = productCard.querySelector('product-form');
          const uom = productForm ? productForm.getAttribute('data-uom') : productCard.getAttribute('data-uom');
          const conversionFactor = productForm ? productForm.getAttribute('data-conversion-factor') : productCard.getAttribute('data-conversion-factor');
          const overrideCupValue = productForm ? productForm.getAttribute('data-overrideCupValue') : productCard.getAttribute('data-overrideCupValue');
          const locationUnitPrice = locationUnitPrices[pickingStore];
          
          if (this.hideUnitPriceDisplayForEachProduce(productCard, uom, unitPriceDisplay)) {
            // Fruit/veg sold as each already shows the each price in the main price.
          } else if (uom && uom.trim()) {
            // Unit price display logic (matching old code):
            // if (!compareAtPrice || overrideCupValue == "Yes") → calculate
            // else → use compareAtPrice (now unit_price_location_XX)
            const priceFloat = parseFloat(locationPrice);
            const unitPriceFloat = locationUnitPrice ? parseFloat(locationUnitPrice) : 0;
            const cleanUom = uom.trim().replace(/^\/\s*/, 'per ').replace(/^per\s+per\s+/, 'per ');
            
            // If no unit price OR override is "Yes", calculate
            if (!unitPriceFloat || unitPriceFloat === 0 || overrideCupValue === "Yes") {
              if (conversionFactor) {
                const conversionFloat = parseFloat(conversionFactor);
                const calculatedUnitPrice = (priceFloat * conversionFloat).toFixed(2);
                unitPriceDisplay.innerHTML = `<small>$${calculatedUnitPrice} ${cleanUom}</small>`;
              } else {
                unitPriceDisplay.innerHTML = '';
              }
            } else {
              // Use the unit price from metafield
              unitPriceDisplay.innerHTML = `<small>$${unitPriceFloat.toFixed(2)} ${cleanUom}</small>`;
            }
          } else {
            unitPriceDisplay.innerHTML = '';
          }
        }
      }
      
      // Update special badge and pricing
      const specialBadge = productCard.querySelector('.special-badge');
      const unitPriceElement = priceWrapper ? priceWrapper.querySelector('.unit_price') : null;
      // Explicit check for special values (matching GraphQL logic)
      const isOnSpecial = locationSpecial === true || locationSpecial === 'true' || locationSpecial === '1' || 
                          locationSpecial === 'Yes' || locationSpecial === 'yes' || 
                          locationSpecial === 'True' ||
                          (locationSpecialText && locationSpecialText !== '-' && locationSpecialText !== 'undefined' && locationSpecialText !== '');
      
      if (isOnSpecial) {
        // Show special badge (always just says "Special")
        if (specialBadge) {
          specialBadge.style.display = 'block';
        }
        
        // Add is-special class to unit_price
        if (unitPriceElement) {
          unitPriceElement.classList.add('is-special');
        }
      } else {
        // Hide badge and remove special class if not on special
        if (specialBadge) {
          specialBadge.style.display = 'none';
        }
        if (unitPriceElement) {
          unitPriceElement.classList.remove('is-special');
        }
      }
      
      // Update additional text — special text wins, then the "Save $X"
      // additional metafield. Both only show while the product is on special.
      if (priceWrapper) {
        const additionalElement = priceWrapper.querySelector('.additional');
        if (additionalElement) {
          if (isOnSpecial && locationSpecialText && locationSpecialText !== '-' && locationSpecialText.trim()) {
            // Show special text when on special
            additionalElement.innerHTML = locationSpecialText;
            additionalElement.classList.add('show-additional');
          } else if (isOnSpecial && locationAdditional && locationAdditional.trim() && locationAdditional !== '-') {
            // Fall back to the additional savings text while on special
            additionalElement.innerHTML = locationAdditional;
            additionalElement.classList.add('show-additional');
          } else {
            additionalElement.innerHTML = '';
            additionalElement.classList.remove('show-additional');
          }
        }
      }
      
      // Update location price inputs for cart
      if (locationPriceInput) {
        locationPriceInput.value = locationPrice;
      }
      if (locationIdInput) {
        locationIdInput.value = pickingStore;
      }
      
      // Enable the form
      if (productIDInput) {
        productIDInput.disabled = false;
      }
      if (submitButton) {
        submitButton.disabled = false;
      }
      
      // Show the card
      productCard.classList.add('card-in-stock');
      this.restoreAddButtonLabel(productCard);
      
      if (this.DEBUG) console.log(`[${productTitle}] ✓ Enabled - Price: $${locationPrice}, Special: ${locationSpecial || 'none'}`);
      
    } catch (e) {
      console.error('Error processing JSON data for product card:', e);
      this.removeProductCard(productCard);
    }
  }

  /**
   * Process legacy product card using the old variant string system
   */
  processLegacyProductCard(productCard, variants) {
    const priceWrapper = productCard.querySelector('.dynamic-price__wrapper');
    const submitButton = productCard.querySelector('.submit-button');
    const productIDInput = productCard.querySelector('input[name="id"]');
    const itemQuantityWrapper = productCard.querySelector('.quick-add__quantity');
    const itemQuantity = productCard.querySelector('.collection-quantity-selector');
    const additionalWrapper = productCard.querySelector('.additional');
    const specialBadge = productCard.querySelector('.special-badge');
    const productForm = productCard.querySelector('product-form');

    if (!priceWrapper) {
      console.warn('⚠️ Price wrapper not found for legacy product card');
      return;
    }

    const partsOfStr = variants.split(';').filter(v => v.trim());
    const pickingStore = this.locationManager.getCurrentLocation();
    const pickingStoreVariant = 's:' + pickingStore;
    
    // Check individual price attribute - if it exists and is empty, remove card immediately
    const individualPriceAttr = productCard.getAttribute(`data-price-location-${pickingStore}`);
    if (individualPriceAttr !== null && (individualPriceAttr === '' || individualPriceAttr === 'null' || parseFloat(individualPriceAttr) <= 0)) {
      console.log(`No valid price in data-price-location-${pickingStore}, removing card`);
      this.removeProductCard(productCard);
      return;
    }

    // Try to find variant for current location, otherwise use first available variant
    let matchingVariantArray = null;
    let fallbackVariantArray = null;

    for (let i = 0; i < partsOfStr.length; i++) {
      const variantArray = partsOfStr[i].split(',');
      if (!variantArray[4]) continue; // Skip if no variant ID
      
      if (variantArray[0].trim() === pickingStoreVariant.trim()) {
        matchingVariantArray = variantArray;
        break;
      }
      
      // Store first valid variant as fallback
      if (!fallbackVariantArray) {
        fallbackVariantArray = variantArray;
      }
    }

    // Use matching variant or fallback to first available
    const variantArray = matchingVariantArray || fallbackVariantArray;

    if (!variantArray) {
      console.warn('⚠️ No valid variants found for legacy product card');
      this.removeProductCard(productCard);
      return;
    }

    // Check if location-specific pricing is available in JSON data attributes (fast path for search/predictive search)
    let locationPrice = null;
    let locationSpecial = null;
    let locationSpecialText = null;
    
    try {
      const locationPricesJson = productCard.getAttribute('data-location-prices');
      const locationSpecialsJson = productCard.getAttribute('data-location-specials');
      const locationSpecialTextsJson = productCard.getAttribute('data-location-special-texts');
      
      if (locationPricesJson) {
        const locationPrices = JSON.parse(locationPricesJson);
        locationPrice = locationPrices[pickingStore];
      }
      
      if (locationSpecialsJson) {
        const locationSpecials = JSON.parse(locationSpecialsJson);
        locationSpecial = locationSpecials[pickingStore];
      }
      
      if (locationSpecialTextsJson) {
        const locationSpecialTexts = JSON.parse(locationSpecialTextsJson);
        locationSpecialText = locationSpecialTexts[pickingStore];
      }
    } catch (e) {
      // JSON parsing failed, will use fallback
      console.warn('Failed to parse location data JSON:', e);
    }
    
    // If the variant string doesn't encode this store's price directly, the JSON
    // price attribute (data-location-prices[pickingStore]) is the only trusted
    // source for the selected store's price. fallbackVariantArray belongs to a
    // DIFFERENT store - its price must never be shown/sold as-is (no synchronous
    // GraphQL fetch happens here, so "wait for GraphQL" would mean showing the
    // wrong price in the meantime; hide the card instead).
    if (!matchingVariantArray && fallbackVariantArray) {
      const priceFloat = parseFloat(locationPrice);
      const hasValidLocationPrice = locationPrice && locationPrice !== '' && locationPrice !== 'null' &&
        !isNaN(priceFloat) && priceFloat > 0;

      if (hasValidLocationPrice) {
        console.log(`Using data attributes for location ${pickingStore}`);
        variantArray[5] = Math.round(priceFloat * 100); // Convert to cents

        // Override special with location-specific special from JSON
        if (locationSpecial && locationSpecial !== '' && locationSpecial !== 'null') {
          variantArray[2] = locationSpecial;
        }

        // Override special text if available
        if (locationSpecialText && locationSpecialText !== '' && locationSpecialText !== 'null') {
          variantArray[3] = locationSpecialText;
        }
      } else {
        console.log(`No valid price for store ${pickingStore} in legacy card data (fallback variant belongs to a different store); hiding card`);
        this.removeProductCard(productCard);
        return;
      }
    }

    const special = variantArray[2];
    const additional = variantArray[3];
    const price = parseInt(variantArray[5]);
    const unitPrice = parseInt(variantArray[6]);
    const conversionFactor = productCard.getAttribute('data-conversion-factor');
    const uom = productCard.getAttribute('data-uom');
    const hideInState = productForm?.dataset.hideinstate || '';
    const overrideCupValue = productCard.getAttribute('data-overridecupvalue');
    const variantId = variantArray[4];
    const quantityAvailable = parseInt(variantArray[7]);
    
    // Check if product should be hidden in current location
    const currentState = pickingStore.toString();
    if (hideInState && hideInState !== '-' && hideInState.includes(currentState)) {
      console.log(`Product hidden in location ${currentState}, removing card`);
      this.removeProductCard(productCard);
      return;
    }
    
    // Remove card if no valid price for current location
    if (!price || price <= 0 || isNaN(price)) {
      console.log(`No valid price for location ${currentState}, removing card`);
      this.removeProductCard(productCard);
      return;
    }
    
    // Remove card if no quantity available
    if (quantityAvailable <= 0 || isNaN(quantityAvailable)) {
      console.log(`Product sold out, removing card`);
      this.removeProductCard(productCard);
      return;
    }

    // Update variant ID
    if (productIDInput) {
      productIDInput.value = variantId;
      productIDInput.disabled = false; // Enable the input
    }
    productCard.setAttribute('data-variant-id', variantId);

        // Update quantity selector
        if (itemQuantityWrapper && itemQuantity) {
          itemQuantityWrapper.setAttribute('data-id', variantId);
          itemQuantity.value = 0;
          
          if (productCard.hasAttribute('data-max-quantity')) {
            const maxQuantity = productCard.getAttribute('data-max-quantity');
            itemQuantity.setAttribute('max', maxQuantity);
          } else {
            itemQuantity.setAttribute('max', quantityAvailable.toString());
          }
        }

        // Update price display
        const fromPriceElement = priceWrapper.querySelector('.sale .from_price');
        const unitPricePerElement = priceWrapper.querySelector('.sale .unit_price_per');
        const unitPriceElement = priceWrapper.querySelector('.unit_price');

        if (fromPriceElement) {
          const formattedPrice = this.formatPrice(price);
          // Main price should never have UOM - just the dollar amount
          fromPriceElement.innerHTML = formattedPrice;
        }

        // Handle special pricing classes
        if (special && special !== 'Default') {
          // Add is-special class to unit_price element
          if (unitPriceElement) {
            unitPriceElement.classList.add('is-special');
            
            // Check if product is fruit or veg and add price-special-color class
            const productType = productCard.getAttribute('data-product-type') || '';
            if (productType.toLowerCase().includes('fruit') || productType.toLowerCase().includes('veg')) {
              unitPriceElement.classList.add('price-special-color');
              console.log(`   Added price-special-color class for ${productType}`);
            }
            
            console.log(`   Added is-special class for special: ${special}`);
          }
          
          // Handle special savings display in additional wrapper
          if (additionalWrapper) {
            // Try to parse the special value as a savings amount
            const savingsMatch = special.toString().match(/[\d.]+/);
            if (savingsMatch) {
              const savingsAmount = parseFloat(savingsMatch[0]);
              if (savingsAmount > 0) {
                additionalWrapper.innerHTML = `Save $${savingsAmount.toFixed(2)}`;
                additionalWrapper.classList.add('show-additional');
                console.log(`   Set special savings: Save $${savingsAmount.toFixed(2)}`);
              }
            } else if (additional) {
              // If special is not a savings amount and there's additional info, show additional
              additionalWrapper.innerHTML = additional;
              additionalWrapper.classList.add('show-additional');
            } else {
              // Show the special value as additional info
              additionalWrapper.innerHTML = special;
              additionalWrapper.classList.add('show-additional');
            }
          }
        } else {
          // Remove special classes if not on special
          if (unitPriceElement) {
            unitPriceElement.classList.remove('is-special', 'price-special-color');
          }
          
          // Not on special: hide the additional "Save $X" text
          if (additionalWrapper) {
            additionalWrapper.innerHTML = '';
            additionalWrapper.classList.remove('show-additional');
          }
        }

        // Update unit price (price by UOM) - supports both old (.unit_price_per) and new (.unit_price_display)
        const unitPriceDisplayElement = priceWrapper.querySelector('.unit_price_display');
        const targetElement = unitPriceDisplayElement || unitPricePerElement;
        
        if (targetElement) {
          console.log(`   Unit price calculation: unitPrice=${unitPrice}, overrideCup=${overrideCupValue}, uom=${uom}, conversion=${conversionFactor}`);
          if (this.hideUnitPriceDisplayForEachProduce(productCard, uom, targetElement)) {
            console.log('   Hidden redundant each unit price for produce card');
            return;
          }
          
          // Fallback to "per kg" if UOM is empty (matching old code logic)
          const rawUom = (uom && uom !== '' && uom !== 'null') ? uom : 'per kg';
          const cleanUom = rawUom.replace(/^\/\s*/, 'per ').replace(/^per\s+per\s+/, 'per ').replace('each', 'ea');
          
          if (!unitPrice || overrideCupValue === 'Yes') {
            const priceByUOM = (price * parseFloat(conversionFactor || 1)) / 100;
            targetElement.innerHTML = `<small>$${priceByUOM.toFixed(2)} ${cleanUom}</small>`;
            console.log(`   Set calculated unit price: $${priceByUOM.toFixed(2)} ${cleanUom}`);
          } else {
            const unitPriceFormatted = this.formatPrice(unitPrice);
            targetElement.innerHTML = `<small>${unitPriceFormatted} ${cleanUom}</small>`;
            console.log(`   Set metafield unit price: ${unitPriceFormatted} ${cleanUom}`);
          }
        } else {
          console.warn('   No target element found for unit price display');
        }

        // Remove price-visually-hidden class to make price visible
        if (priceWrapper && priceWrapper.classList.contains('price-visually-hidden')) {
          priceWrapper.classList.remove('price-visually-hidden');
          console.log('   Removed price-visually-hidden class');
        }

        // Update special badge (always just says "Special")
        if (special && special !== 'Default' && specialBadge) {
          specialBadge.innerHTML = 'Special';
          specialBadge.classList.add('show-additional');
        }

        // Set location-specific properties for cart
        const locationPriceInput = productCard.querySelector('[data-location-price]');
        const locationIdInput = productCard.querySelector('[data-location-id]');
        
        if (locationPriceInput) {
          // Convert cents to dollars
          const locationPriceInDollars = (price / 100).toFixed(2);
          locationPriceInput.value = locationPriceInDollars;
          console.log(`   Set location price: $${locationPriceInDollars}`);
        }
        
        if (locationIdInput) {
          locationIdInput.value = pickingStore;
        }

        // Show the card and enable submit button (price was validated above)
        productCard.classList.add('card-in-stock');
        if (submitButton) {
          submitButton.disabled = false;
        }
        this.restoreAddButtonLabel(productCard);
  }

  /**
   * Add product to batch queue for efficient processing
   */
  addToBatchQueue(productHandle, productCard) {
    this.batchQueue.push({ handle: productHandle, card: productCard });
    
    // Clear existing timeout
    if (this.batchTimeout) {
      clearTimeout(this.batchTimeout);
    }
    
    // Set new timeout to process batch
    this.batchTimeout = setTimeout(() => {
      this.processBatchQueue();
    }, this.batchDelay);
  }

  /**
   * Process the batch queue
   */
  async processBatchQueue() {
    if (this.batchQueue.length === 0 || this.isProcessing) {
      return;
    }

    this.isProcessing = true;
    
    try {
      // Get unique product handles
      const uniqueHandles = [...new Set(this.batchQueue.map(item => item.handle))];
      if (this.DEBUG) console.log(`Processing batch of ${uniqueHandles.length} unique products...`);

      // Fetch product data
      const productsData = await this.locationManager.getBatchProducts(uniqueHandles);
      
      if (!productsData || productsData.length === 0) {
        console.warn('⚠️ No product data received for batch');
        return;
      }

      // Update product cards with fetched data
      this.updateProductCards(this.batchQueue, productsData);
      
    } catch (error) {
      console.error('Error processing batch queue:', error);
    } finally {
      this.isProcessing = false;
      this.batchQueue = []; // Clear the queue
    }
  }

  /**
   * Update product cards with fetched data
   */
  updateProductCards(batch, productsData) {
    batch.forEach(({ handle, card }) => {
      const productData = productsData.find(p => p.handle === handle);
      if (productData) {
        this.updateProductCard(card, productData);
      } else {
        console.warn(`⚠️ No data found for product: ${handle}`);
      }
    });
  }

  /**
   * Update a single product card with product data
   */
  updateProductCard(productCard, productData) {
    try {
      const currentLocation = this.locationManager.currentLocation;
      
      console.log(`Updating product card for: ${productData.title}`);
      
      // Get location-specific metafields
      const locationPriceKey = `price_location_${currentLocation}`;
      const locationSpecialKey = `special_location_${currentLocation}`;
      const locationSpecialTextKey = `special_text_location_${currentLocation}`;
      const locationAdditionalKey = `additional_location_${currentLocation}`;
      
      const priceMetafield = productData.metafields?.find(m => m.key === locationPriceKey);
      const specialMetafield = productData.metafields?.find(m => m.key === locationSpecialKey);
      const specialTextMetafield = productData.metafields?.find(m => m.key === locationSpecialTextKey);
      const additionalMetafield = productData.metafields?.find(m => m.key === locationAdditionalKey);
      const uomMetafield = productData.metafields?.find(m => m.key === 'uom');
      const conversionMetafield = productData.metafields?.find(m => m.key === 'conversion');
      const overrideCupMetafield = productData.metafields?.find(m => m.key === 'overridecup');
      const hideInStateMetafield = productData.metafields?.find(m => m.key === 'hideInState');

      console.log(`   Found ${productData.metafields?.length || 0} metafields`);
      if (priceMetafield) console.log(`   Price: ${priceMetafield.value}`);
      if (uomMetafield) console.log(`   UOM: ${uomMetafield.value}`);

      // Check if product should be hidden in current location
      const hideInState = hideInStateMetafield?.value || '';
      if (hideInState && hideInState !== '-' && hideInState.includes(currentLocation.toString())) {
        console.log(`Product hidden in location ${currentLocation}, removing card`);
        this.removeProductCard(productCard);
        return;
      }
      
      // Find the appropriate variant for this location
      const locationVariant = this.findLocationVariant(productData, currentLocation);
      
      if (!locationVariant) {
        console.warn(`⚠️ No variant found for location ${currentLocation} in product: ${productData.title}`);
        this.removeProductCard(productCard);
        return;
      }
      
      // Check if product has valid price for current location (using priceMetafield already declared above)
      const locationPrice = priceMetafield?.value;
      
      // Remove card if no valid price or not available
      if (!locationPrice || locationPrice === '' || locationPrice === '0' || parseFloat(locationPrice) <= 0) {
        console.log(`No valid price for location ${currentLocation}, removing card`);
        this.removeProductCard(productCard);
        return;
      }
      
      // Remove card if not available for sale
      if (!locationVariant.availableForSale || locationVariant.quantityAvailable <= 0) {
        console.log(`Product not available or sold out, removing card`);
        this.removeProductCard(productCard);
        return;
      }

      // Update form elements (may hide the card if the selected-store price fails
      // a final defensive check; bail out before touching price/unit displays if so)
      const formOk = this.updateFormElements(productCard, locationVariant, productData, priceMetafield, currentLocation);
      if (!formOk) return;

      // Update price display
      this.updatePriceDisplay(productCard, priceMetafield, locationVariant, uomMetafield, specialMetafield, productData);

      // Update unit price
      this.updateUnitPrice(productCard, locationVariant, priceMetafield, uomMetafield, conversionMetafield, overrideCupMetafield, productData);

      // Update special badge (pass specialTextMetafield for fallback check)
      this.updateSpecialBadge(productCard, specialMetafield, specialTextMetafield);

      // Update additional info
      this.updateAdditionalInfo(productCard, additionalMetafield, specialMetafield, specialTextMetafield);

      // Make price wrapper visible (remove hidden class)
      const priceWrapper = productCard.querySelector('.dynamic-price__wrapper');
      if (priceWrapper && priceWrapper.classList.contains('price-visually-hidden')) {
        priceWrapper.classList.remove('price-visually-hidden');
        console.log('   Removed price-visually-hidden class');
      }

      // Mark as processed
      productCard.setAttribute('data-processed', 'true');
      
      console.log(`Successfully updated product card: ${productData.title}`);
      
    } catch (error) {
      console.error('Error updating product card:', error, productData);
    }
  }

  /**
   * Find the appropriate variant for the current location
   */
  findLocationVariant(productData, currentLocation) {
    if (!productData.variants || !productData.variants.edges) {
      return null;
    }

    // First, try to find a variant with the location in its title
    const locationVariant = productData.variants.edges.find(({ node: variant }) => {
      return variant.title && variant.title.toLowerCase().includes(`s:${currentLocation}`);
    });

    if (locationVariant) {
      return locationVariant.node;
    }

    // Fallback to first available variant
    const firstAvailable = productData.variants.edges.find(({ node: variant }) => {
      return variant.availableForSale && variant.quantityAvailable > 0;
    });

    return firstAvailable ? firstAvailable.node : productData.variants.edges[0]?.node;
  }

  /**
   * Update form elements with variant data
   * @returns {boolean} false if the card was hidden due to a missing/invalid selected-store price
   */
  updateFormElements(productCard, variant, productData, priceMetafield, currentLocation) {
    const productIDInput = productCard.querySelector('input[name="id"]');
    const itemQuantity = productCard.querySelector('.collection-quantity-selector');
    const submitButton = productCard.querySelector('.submit-button');
    const locationPriceInput = productCard.querySelector('[data-location-price]');

    // Update variant ID
    const variantId = variant.id.split('/').pop();
    if (productIDInput) {
      productIDInput.value = variantId;
    }
    productCard.setAttribute('data-variant-id', variantId);

    // Update quantity selector
    if (itemQuantity) {
      itemQuantity.value = 0;
      
      const maxQuantity = productCard.getAttribute('data-max-quantity') || 
                         variant.quantityAvailable || 
                         20;
      itemQuantity.setAttribute('max', maxQuantity.toString());
    }
    
    // Store location-specific price for cart. Selected-store metafield only -
    // ordinary products must never fall back to Shopify's base variant price
    // or another store's price. Defensive backstop even though the caller
    // already validated this price before reaching this method.
    if (locationPriceInput) {
      const locationPrice = priceMetafield?.value;
      if (!locationPrice || parseFloat(locationPrice) <= 0) {
        console.error(`[ProductCardData] updateFormElements: missing/invalid price_location_${currentLocation} for "${productData?.title || 'unknown'}" - hiding card instead of using base/other-store price.`);
        this.removeProductCard(productCard);
        return false;
      }
      locationPriceInput.value = locationPrice;
      productCard.setAttribute('data-location-price', locationPrice);
    }

    // Show the card and enable submit button (availability was validated before calling this method)
    productCard.classList.add('card-in-stock');
    if (submitButton) {
      submitButton.disabled = false;
    }
    this.restoreAddButtonLabel(productCard);
    return true;
  }

  /**
   * Update price display
   */
  updatePriceDisplay(productCard, priceMetafield, variant, uomMetafield, specialMetafield, productData) {
    const priceWrapper = productCard.querySelector('.dynamic-price__wrapper');
    if (!priceWrapper) {
      console.warn(' Price wrapper not found');
      return;
    }

    const fromPriceElement = priceWrapper.querySelector('.sale .from_price');
    const unitPriceElement = priceWrapper.querySelector('.unit_price');
    if (!fromPriceElement) {
      console.warn(' From price element not found');
      return;
    }

    // Selected-store metafield price only. Ordinary products must never display
    // Shopify's base variant price - missing/invalid price hides the card.
    const locationPriceValue = priceMetafield?.value;
    if (!locationPriceValue || parseFloat(locationPriceValue) <= 0) {
      console.error(`[ProductCardData] updatePriceDisplay: missing/invalid selected-store price for "${productData?.title || 'unknown'}" - hiding card.`);
      this.removeProductCard(productCard);
      return;
    }
    const priceInCents = parseFloat(locationPriceValue) * 100;
    console.log(`   Using metafield price: $${(priceInCents/100).toFixed(2)}`);

    if (priceInCents > 0) {
      const formattedPrice = this.formatPrice(priceInCents);
      
      // Main price should never have UOM - just the dollar amount
      fromPriceElement.innerHTML = formattedPrice;
      
      // Handle special pricing classes
      if (specialMetafield && specialMetafield.value && specialMetafield.value !== 'Default') {
        // Add is-special class to unit_price element
        if (unitPriceElement) {
          unitPriceElement.classList.add('is-special');
          
          // Check if product is fruit or veg and add price-special-color class
          const productType = productData.productType || '';
          if (productType.toLowerCase().includes('fruit') || productType.toLowerCase().includes('veg')) {
            unitPriceElement.classList.add('price-special-color');
            console.log(`   Added price-special-color class for ${productType}`);
          }
          
          console.log(`   Added is-special class for special: ${specialMetafield.value}`);
        }
      } else {
        // Remove special classes if not on special
        if (unitPriceElement) {
          unitPriceElement.classList.remove('is-special', 'price-special-color');
        }
      }
      
      console.log(`   Set price to: ${fromPriceElement.innerHTML}`);
    } else {
      console.warn(' No valid price found');
    }
  }

  /**
   * Update unit price display
   */
  updateUnitPrice(productCard, variant, priceMetafield, uomMetafield, conversionMetafield, overrideCupMetafield, productData = null) {
    const priceWrapper = productCard.querySelector('.dynamic-price__wrapper');
    if (!priceWrapper) return;

    // Support both old (.unit_price_per) and new (.unit_price_display)
    const unitPriceDisplayElement = priceWrapper.querySelector('.unit_price_display');
    const unitPricePerElement = priceWrapper.querySelector('.sale .unit_price_per');
    const targetElement = unitPriceDisplayElement || unitPricePerElement;
    
    if (!targetElement) return;

    const uom = uomMetafield?.value || '';
    const conversion = parseFloat(conversionMetafield?.value || '1');
    const overrideCup = overrideCupMetafield?.value || '';

    if (this.hideUnitPriceDisplayForEachProduce(productCard, uom, targetElement, productData)) {
      return;
    }

    // Fallback to "per kg" if UOM is empty (matching old code logic)
    const rawUom = uom && uom !== '' ? uom : 'per kg';
    const cleanUom = rawUom.replace(/^\/\s*/, 'per ').replace(/^per\s+per\s+/, 'per ').replace('each', 'ea');

    // Selected-store metafield price only - never Shopify's base variant price.
    const locationPriceValue = priceMetafield?.value;
    let priceInCents = 0;
    if (locationPriceValue && parseFloat(locationPriceValue) > 0) {
      priceInCents = parseFloat(locationPriceValue) * 100;
    }

    if (priceInCents > 0) {
      if (overrideCup === 'Yes' || !variant.compareAtPrice) {
        // Calculate unit price using conversion factor
        const unitPrice = (priceInCents * conversion) / 100;
        targetElement.innerHTML = `<small>$${unitPrice.toFixed(2)} ${cleanUom}</small>`;
        console.log(`   Set unit price to: $${unitPrice.toFixed(2)} ${cleanUom}`);
      } else if (variant.compareAtPrice) {
        // Use compare at price as unit price
        const unitPrice = parseFloat(variant.compareAtPrice.amount);
        targetElement.innerHTML = `<small>$${unitPrice.toFixed(2)} ${cleanUom}</small>`;
        console.log(`   Set unit price to: $${unitPrice.toFixed(2)} ${cleanUom} (from compareAt)`);
      }
    }
  }

  /**
   * Update special badge (always just says "Special")
   */
  updateSpecialBadge(productCard, specialMetafield, specialTextMetafield) {
    const specialBadge = productCard.querySelector('.special-badge');
    if (!specialBadge) return;

    const special = specialMetafield?.value;
    const specialText = specialTextMetafield?.value;
    const isOnSpecial = special === true || special === 'true' || special === '1' ||
                        special === 'Yes' || special === 'yes' || 
                        special === 'True' ||
                        (specialText && specialText !== '-' && specialText !== 'undefined' && specialText !== '');
    
    if (isOnSpecial) {
      specialBadge.innerHTML = 'Special';
      specialBadge.classList.add('show-additional');
      console.log(`   Set special badge to "Special"`);
    } else {
      specialBadge.classList.remove('show-additional');
    }
  }

  /**
   * Update additional info - shows special text from special_text_location_XX metafield
   */
  updateAdditionalInfo(productCard, additionalMetafield, specialMetafield, specialTextMetafield) {
    const additionalWrapper = productCard.querySelector('.additional');
    if (!additionalWrapper) return;

    const special = specialMetafield?.value;
    const specialText = specialTextMetafield?.value;
    const isOnSpecial = special === true || special === 'true' || special === '1' ||
                        special === 'Yes' || special === 'yes' || 
                        special === 'True' ||
                        (specialText && specialText !== '-' && specialText !== 'undefined' && specialText !== '');

    // If product is on special, handle special savings display
    if (isOnSpecial) {
      // Try to parse the special text as a savings amount (e.g., "Save 2.50" or "2.50")
      if (specialText) {
        const savingsMatch = specialText.toString().match(/[\d.]+/);
        if (savingsMatch) {
          const savingsAmount = parseFloat(savingsMatch[0]);
          if (savingsAmount > 0) {
            additionalWrapper.innerHTML = `Save $${savingsAmount.toFixed(2)}`;
            additionalWrapper.classList.add('show-additional');
            console.log(`   Set special savings: Save $${savingsAmount.toFixed(2)}`);
            return;
          }
        }
        // If not a savings amount, show the special text as-is
        additionalWrapper.innerHTML = specialText;
        additionalWrapper.classList.add('show-additional');
        console.log(`   Set special text: ${specialText}`);
        return;
      }
      
      // If special is set but no special text, try to use additional field
      if (additionalMetafield && additionalMetafield.value) {
        additionalWrapper.innerHTML = additionalMetafield.value;
        additionalWrapper.classList.add('show-additional');
        console.log(`   Set additional info for special: ${additionalMetafield.value}`);
        return;
      }
    }

    // Not on special: the additional "Save $X" text would be misleading, hide it.
    additionalWrapper.innerHTML = '';
    additionalWrapper.classList.remove('show-additional');
  }

  /**
   * Format price from cents to currency string
   */
  formatPrice(priceInCents) {
    return `$${(priceInCents / 100).toFixed(2)}`;
  }

  /**
   * Process main product form on product pages
   */
  async processMainProductForm() {
    console.log('Looking for main product form...');
    const productForm = document.querySelector('product-form form[data-product-id]');
    
    if (!productForm) {
      console.log('No main product form found (not on a product page)');
      return; // Not on a product page or form not found
    }

    const productCard = productForm.closest('.product-item');
    if (productCard?.hasAttribute('data-location-prices')) {
      if (this.DEBUG) console.log('Main product form already processed from Liquid location payload');
      return;
    }

    const productHandle = this.getProductHandleFromURL();
    if (!productHandle) {
      console.warn('Could not determine product handle from URL');
      return;
    }

    if (this.DEBUG) console.log(`Processing main product form for: ${productHandle}`);

    try {
      const productData = await this.locationManager.getProduct(productHandle);
      
      if (!productData) {
        console.error(`Failed to fetch product data for ${productHandle}`);
        return;
      }

      const currentLocation = this.locationManager.currentLocation;
      const priceKey = `price_location_${currentLocation}`;
      const hideInStateKey = 'hideInState';

      // Handle nested productByHandle structure from locationManager.getProduct()
      const product = productData.productByHandle || productData;

      const validMetafields = product.metafields?.filter(m => m && m.key) || [];
      
      // DEBUG: Log all price metafields
      console.log(`📊 Product page metafields for ${productHandle}:`);
      const priceMetafields = validMetafields.filter(m => m.key.startsWith('price_location_'));
      priceMetafields.forEach(m => {
        console.log(`   - ${m.key}: ${m.value}`);
      });
      
      const priceMetafield = validMetafields.find(m => m.key === priceKey);
      const hideInStateMetafield = validMetafields.find(m => m.key === hideInStateKey);

      // Check if product is hidden at this location
      if (hideInStateMetafield?.value) {
        const hiddenStates = hideInStateMetafield.value.split(',').map(s => s.trim());
        if (hiddenStates.includes(currentLocation)) {
          console.log(`Product hidden at location ${currentLocation}`);
          this.disableProductForm(productForm, 'Product not available at your location');
          return;
        }
      }

      // Check if location has inventory (available_locations metafield)
      const availableLocationsMetafield = validMetafields.find(m => m.key === 'available_locations');
      
      if (this.DEBUG) {
        console.log(`[Product Page] ${productHandle} - available_locations metafield:`, availableLocationsMetafield);
        console.log(`[Product Page] ${productHandle} - available_locations value:`, availableLocationsMetafield?.value);
      }
      
      // STRICT CHECK: If metafield is missing, null, empty string, or empty array - HIDE product
      if (!availableLocationsMetafield || !availableLocationsMetafield.value || availableLocationsMetafield.value === '' || availableLocationsMetafield.value === '[]') {
        if (this.DEBUG) console.log(`[Product Page] ${productHandle}: HIDING - available_locations is null/empty`);
        this.disableProductForm(productForm, 'Sold out');
        return;
      }
      
      // Parse and check the array
      try {
        let locationsArray = availableLocationsMetafield.value;
        
        // Parse if string
        if (typeof locationsArray === 'string') {
          locationsArray = JSON.parse(locationsArray);
        }
        
        // Check if array is empty
        if (!Array.isArray(locationsArray) || locationsArray.length === 0) {
          if (this.DEBUG) console.log(`[Product Page] ${productHandle}: HIDING - Empty array`);
          this.disableProductForm(productForm, 'Sold out');
          return;
        }
        
        // Check if current location is in array
        const isAvailableAtLocation = locationsArray.includes(currentLocation) || 
                                     locationsArray.includes(String(currentLocation));
        
        if (!isAvailableAtLocation) {
          if (this.DEBUG) console.log(`[Product Page] ${productHandle}: HIDING - Location ${currentLocation} not in array [${locationsArray.join(', ')}]`);
          this.disableProductForm(productForm, 'Sold out at your location');
          return;
        }
        
        if (this.DEBUG) console.log(`[Product Page] ${productHandle}: ✓ SHOWING - Location ${currentLocation} found in array`);
      } catch (e) {
        if (this.DEBUG) console.warn(`[Product Page] ${productHandle}: HIDING - Failed to parse available_locations:`, e);
        this.disableProductForm(productForm, 'Sold out');
        return;
      }
      
      // Check if product has price at this location (only use metafields).
      // Selected-store price is mandatory - store 1/76 (or any other store) must
      // never be used as a fallback. Missing/invalid price hides the product
      // form entirely rather than showing/selling at another store's price.
      let locationPrice;

      if (priceMetafield?.value && parseFloat(priceMetafield.value) > 0) {
        // Metafield is already in dollars (not cents)
        locationPrice = parseFloat(priceMetafield.value).toFixed(2);
        console.log(`✓ Product page price for location ${currentLocation}:`);
        console.log(`   - Metafield value: $${locationPrice}`);
      } else {
        console.warn(`No valid price found at location ${currentLocation} - hiding product (no fallback to another store's price)`);
        this.disableProductForm(productForm, 'Unavailable at your selected store');
        // Hide the product form entirely
        productForm.style.display = 'none';
        return;
      }

      // Update hidden inputs
      const locationPriceInput = productForm.querySelector('[data-location-price]');
      const locationIdInput = productForm.querySelector('[data-location-id]');
      const variantIdInput = productForm.querySelector('input[name="id"]');

      if (locationPriceInput) {
        locationPriceInput.value = locationPrice;
        console.log(`Set location price: $${locationPrice}`);
      }

      if (locationIdInput) {
        locationIdInput.value = currentLocation;
      }

      if (variantIdInput) {
        variantIdInput.disabled = false;
      }

      // Update visible price display on the product page
      const fromPriceElement = document.querySelector('.from_price');
      const priceElement = document.querySelector('.price-item--regular, .price__regular .price-item');
      
      if (fromPriceElement) {
        fromPriceElement.textContent = `$${locationPrice}`;
        console.log(`Updated .from_price to $${locationPrice}`);
      }
      
      if (priceElement) {
        priceElement.textContent = `$${locationPrice}`;
        console.log(`Updated .price-item to $${locationPrice}`);
      }

      // Update unit price display on product page
      const unitPriceDisplayElement = document.querySelector('.unit_price_display');
      if (unitPriceDisplayElement) {
        const unitPriceMetafield = validMetafields.find(m => m.key === `unit_price_location_${currentLocation}`);
        const conversionMetafield = validMetafields.find(m => m.key === 'conversion');
        const overrideCupMetafield = validMetafields.find(m => m.key === 'overridecup');
        const uomMetafield = validMetafields.find(m => m.key === 'uom');
        
        // Unit price metafield is already in dollars (not cents)
        const unitPriceValue = unitPriceMetafield?.value ? parseFloat(unitPriceMetafield.value) : null;
        const conversionFactor = conversionMetafield?.value;
        const overrideCupValue = overrideCupMetafield?.value;
        const rawUom = uomMetafield?.value || '';
        const price = parseFloat(locationPrice);

        if (this.hideUnitPriceDisplayForEachProduce(productForm.closest('.product-item'), rawUom, unitPriceDisplayElement, product)) {
          console.log('Product page - Hidden redundant each unit price for produce product');
        } else {
        
          // Fallback to "per kg" if UOM is empty (matching old code logic)
          const displayUom = rawUom && rawUom !== "" ? rawUom : "per kg";
          
          if (!unitPriceValue || overrideCupValue === "Yes") {
            // Calculate unit price from price * conversion factor
            if (conversionFactor) {
              const calculatedUnitPrice = (price * parseFloat(conversionFactor)).toFixed(2);
              unitPriceDisplayElement.innerHTML = `<small>$${calculatedUnitPrice} ${displayUom}</small>`;
              console.log(`Product page - Set calculated unit price: $${calculatedUnitPrice} ${displayUom}`);
            }
          } else {
            // Use unit_price metafield (already converted from cents to dollars)
            unitPriceDisplayElement.innerHTML = `<small>$${unitPriceValue.toFixed(2)} ${displayUom}</small>`;
            console.log(`Product page - Set metafield unit price: $${unitPriceValue.toFixed(2)} ${displayUom}`);
          }
        }
      }

      // Add price-loaded class to make price visible
      const priceWrapper = document.querySelector('.dynamic-price__wrapper');
      if (priceWrapper) {
        priceWrapper.classList.remove('price-visually-hidden');
        priceWrapper.classList.add('price-loaded');
        console.log('Added price-loaded class to show correct location price');
      }

      // Reaching here means the product passed the available_locations + price checks above,
      // i.e. it IS available at the picking store. Availability is location-based, not driven by
      // Shopify's global inventory, so enable the form (enableProductForm also restores the add
      // label in case the server rendered the global "Sold out"). Genuinely unavailable cases
      // ("Sold out" / "Sold out at your location") already returned early above.
      this.enableProductForm(productForm);

    } catch (error) {
      console.error('Error processing main product form:', error);
    }
  }

  /**
   * Get product handle from URL
   */
  getProductHandleFromURL() {
    const pathParts = window.location.pathname.split('/');
    const productsIndex = pathParts.indexOf('products');
    
    if (productsIndex !== -1 && pathParts[productsIndex + 1]) {
      return pathParts[productsIndex + 1];
    }

    return null;
  }

  /**
   * Enable product form
   */
  enableProductForm(form) {
    const submitButton = form.querySelector('.product-form__submit');
    if (submitButton) {
      submitButton.disabled = false;
      submitButton.removeAttribute('disabled');
      submitButton.removeAttribute('aria-disabled');
      // Availability is location-based; if the server rendered the global "Sold out" label,
      // restore the add state now that availability at the picking store is confirmed.
      this.restoreAddButtonLabel(form);
      console.log('Product form enabled');
    }
  }

  /**
   * Disable product form with message
   */
  disableProductForm(form, message = 'Unavailable') {
    const submitButton = form.querySelector('.product-form__submit');
    if (submitButton) {
      submitButton.disabled = true;
      submitButton.setAttribute('aria-disabled', 'true');
      
      const buttonText = submitButton.querySelector('.js-go-cart-add-to-cart, span');
      if (buttonText) {
        const originalText = buttonText.textContent;
        if (!originalText.includes(message)) {
          buttonText.textContent = message;
        }
      }
      
      console.log(`Product form disabled: ${message}`);
    }
  }

  /**
   * Get current state for debugging
   */
  getCurrentState() {
    return {
      batchQueueLength: this.batchQueue.length,
      isProcessing: this.isProcessing,
      currentLocation: this.locationManager?.currentLocation
    };
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  window.productCardDataManager = new ProductCardDataManager();
  window.productCardDataManager.init();
  
  // Failsafe: If processing takes too long on SEARCH pages, force-remove loading overlay
  // (Collection pages are managed by graphql-collection-manager.js)
  setTimeout(() => {
    const isSearchPage = document.body.classList.contains('template-search');
    
    if (isSearchPage) {
      const productGrid = document.getElementById('product-grid');
      if (productGrid && !productGrid.classList.contains('product-grid--loaded')) {
        console.warn('⏱️ Search loading overlay timeout - forcing removal');
        productGrid.classList.add('product-grid--loaded');
        
        const resultsLoadingOverlay = document.querySelector('.results-loading-overlay');
        if (resultsLoadingOverlay) {
          resultsLoadingOverlay.style.display = 'none';
        }
      }
    }
  }, 5000); // 5 second timeout
});

// Legacy function compatibility
window.createProductCard = function() {
  if (window.productCardDataManager) {
    window.productCardDataManager.processAllProductCards();
  }
};

window.processProductCard = function(productCard) {
  if (window.productCardDataManager) {
    window.productCardDataManager.processProductCard(productCard);
  }
}; 